import { CommonModule, DOCUMENT, isPlatformBrowser } from "@angular/common";
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
} from "@angular/core";
import { RouterModule } from "@angular/router";

export interface DsNavbarItem {
  label: string;
  route?: string;
  href?: string;
  exact?: boolean;
  disabled?: boolean;
  target?: "_self" | "_blank";
  children?: DsNavbarItem[];
}

const BOTTOM_SHEET_BODY_CLASSES = ["bottom-sheet-open", "donate-sheet-open"];

@Component({
  selector: "app-ds-navbar",
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: "./navbar.component.html",
  styleUrls: ["./navbar.component.scss"]})
export class DsNavbarComponent implements OnInit, OnDestroy {
  @Input() brandLabel = "Rhyno";
  @Input() brandRoute = "/";
  @Input() brandHref?: string;
  @Input() showBrand = true;
  @Input() sticky = true;
  @Input() embedded = false;
  @Input() height = "var(--ds-navbar-height)";
  @Input() mobileMenuLabel = "Abrir menu";
  @Input() closeOnItemSelect = true;
  @Input() items: DsNavbarItem[] = [];

  @Output() itemSelected = new EventEmitter<DsNavbarItem>();

  mobileMenuOpen = false;
  openedDropdownLabel: string | null = null;
  private bodyClassObserver: MutationObserver | null = null;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  ngOnInit(): void {
    this.setupBottomSheetBodyObserver();
  }

  ngOnDestroy(): void {
    this.bodyClassObserver?.disconnect();
    this.bodyClassObserver = null;
  }

  trackByLabel(index: number, item: DsNavbarItem): string {
    return `${index}-${item.label}`;
  }

  itemExact(item: DsNavbarItem): boolean {
    return !!item.exact;
  }

  hasChildren(item: DsNavbarItem): boolean {
    return !!item.children?.length;
  }

  isDropdownOpen(item: DsNavbarItem): boolean {
    return this.openedDropdownLabel === item.label;
  }

  toggleMobileMenu(): void {
    if (this.isBottomSheetOpen()) {
      this.mobileMenuOpen = false;
      this.openedDropdownLabel = null;
      return;
    }

    this.mobileMenuOpen = !this.mobileMenuOpen;

    if (!this.mobileMenuOpen) {
      this.openedDropdownLabel = null;
    }
  }

  toggleDropdown(item: DsNavbarItem, event?: Event): void {
    if (!this.hasChildren(item) || item.disabled) {
      return;
    }

    event?.preventDefault();
    event?.stopPropagation();

    this.openedDropdownLabel = this.isDropdownOpen(item) ? null : item.label;
  }

  onItemClick(item: DsNavbarItem): void {
    if (item.disabled) {
      return;
    }

    this.itemSelected.emit(item);

    if (this.closeOnItemSelect) {
      this.mobileMenuOpen = false;
      this.openedDropdownLabel = null;
    }
  }

  private setupBottomSheetBodyObserver(): void {
    if (!isPlatformBrowser(this.platformId) || typeof MutationObserver === "undefined") {
      return;
    }

    const syncMenuState = () => {
      if (!this.isBottomSheetOpen()) {
        return;
      }

      this.mobileMenuOpen = false;
      this.openedDropdownLabel = null;
    };

    syncMenuState();

    this.bodyClassObserver?.disconnect();
    this.bodyClassObserver = new MutationObserver(() => {
      syncMenuState();
    });

    this.bodyClassObserver.observe(this.document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  private isBottomSheetOpen(): boolean {
    const bodyClassList = this.document.body?.classList;
    if (!bodyClassList) {
      return false;
    }

    return BOTTOM_SHEET_BODY_CLASSES.some((className) =>
      bodyClassList.contains(className),
    );
  }
}